/**
 * Turbine — extracts work via isentropic expansion.
 *
 * h_out = h_in - (h_in - h_s(P_out, s_in)) × η
 * W = ṁ × (h_in - h_out)
 *
 * Exit-wetness diagnostics (READ-ONLY, derived from the already-solved outlet
 * state — they do NOT influence deltaP/deltaH/the expansion calc):
 *   exitQuality        — vapour quality x at the outlet (P_out, h_out).
 *                        Follows the fluidAdapter sentinel convention:
 *                        0..1 two-phase, -1 subcooled, >1 (==2) superheated.
 *   wetnessFraction    — liquid mass fraction (1 - x), only physical in the
 *                        two-phase region; reported as 0 for a superheated exit.
 *   wetnessExceedsLimit— 1 when wetnessFraction > BAUMANN_WETNESS_LIMIT (~12%),
 *                        else 0. Baumann wet-steam blade-erosion guideline.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { propsFromPH_SI } from '../core/fluidAdapter';
import { solvePT, solvePS } from 'iapws-if97';

/**
 * Baumann wet-steam erosion guideline: keep last-stage exit wetness below ~12%
 * to limit liquid-droplet erosion of LP turbine blading. Used only to set the
 * advisory `wetnessExceedsLimit` flag — it does not alter the expansion.
 */
export const BAUMANN_WETNESS_LIMIT = 0.12;

/**
 * Derive exit-wetness diagnostics from the solved outlet state (P_out, h_out).
 * Pure/read-only: it reads fluid properties at the outlet and returns numbers.
 *
 * @param P_out_Pa  Outlet pressure in Pa
 * @param h_out_Jkg Outlet (actual) enthalpy in J/kg
 */
function exitWetnessDiagnostics(P_out_Pa: number, h_out_Jkg: number): {
  exitQuality: number;
  wetnessFraction: number;
  wetnessExceedsLimit: number;
} {
  // Quality follows the fluidAdapter convention: 0..1 two-phase, -1 subcooled,
  // 2 superheated (the >1 sentinel). We pass the sentinel through unchanged.
  const x = propsFromPH_SI(P_out_Pa, h_out_Jkg).x;

  // Wetness is only physically meaningful in the two-phase region (0 ≤ x ≤ 1).
  // - Superheated exit (x > 1 sentinel): no liquid present → wetness 0.
  // - Subcooled exit (x < 0 sentinel, e.g. -1): fully liquid → wetness 1.
  let wetnessFraction: number;
  if (x >= 0 && x <= 1) {
    wetnessFraction = 1 - x; // bounded in [0, 1] by construction
  } else if (x > 1) {
    wetnessFraction = 0; // superheated — dry
  } else {
    wetnessFraction = 1; // subcooled — fully liquid
  }

  const wetnessExceedsLimit = wetnessFraction > BAUMANN_WETNESS_LIMIT ? 1 : 0;

  return { exitQuality: x, wetnessFraction, wetnessExceedsLimit };
}

export class NSTurbine implements NSComponent {
  id: string;
  type = 'turbine';
  params: Record<string, any>;

  constructor(id: string, params: {
    pressureRatio?: number;
    P_outlet?: number;      // Pa — fixed outlet pressure
    efficiency?: number;
  }) {
    this.id = id;
    this.params = { pressureRatio: 10, efficiency: 0.85, ...params };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, _dt: number): ComponentUpdate {
    const { pressureRatio, P_outlet, efficiency } = this.params;

    const P_in = inlet.P;
    const h_in = inlet.h;
    const P_out = P_outlet ?? P_in / pressureRatio;

    // Isentropic expansion: find h_s at P_out with s = s_in
    try {
      const props_in = propsFromPH_SI(P_in, h_in);
      const s_in_kJkgK = props_in.s / 1000; // J/(kg·K) → kJ/(kg·K)
      const P_out_MPa = P_out / 1e6;

      const state_s = solvePS(P_out_MPa, s_in_kJkgK);
      const h_s = state_s.enthalpy * 1000; // kJ/kg → J/kg

      const h_actual = h_in - (h_in - h_s) * efficiency;
      const deltaH = h_actual - h_in; // Negative (enthalpy decreases)
      const deltaP = P_out - P_in;    // Negative (pressure drops)

      // READ-ONLY exit-wetness diagnostics from the solved outlet state.
      // Does not feed back into deltaP/deltaH/the expansion above.
      const { exitQuality, wetnessFraction, wetnessExceedsLimit } =
        exitWetnessDiagnostics(P_out, h_actual);

      const metadata = {
        W_turbine: -deltaH,
        P_out,
        h_out: h_actual,
        exitQuality,
        wetnessFraction,
        wetnessExceedsLimit,
      };
      // Side-channel store for the UI sync (stateSync.ts) — read-only mirror of
      // this update's metadata. Never read by the solver, so it cannot alter the
      // expansion/convergence. Mirrors the NSPump._lastMetadata convention.
      this.params._lastMetadata = metadata;

      return {
        deltaP,
        deltaH,
        momentumSource: 0,
        energySource: 0,
        metadata,
      };
    } catch {
      // Fallback: estimate work from ideal gas expansion if isentropic lookup fails.
      // W/mdot ≈ cp × T_in × (1 - (P_out/P_in)^((γ-1)/γ)) × η
      // Use water vapour defaults: cp ≈ 2000 J/(kg·K), γ ≈ 1.3
      const T_in_K = 500; // approximate, since lookup failed
      const gamma = 1.3;
      const cp = 2000;
      const pratio = Math.max(0.01, P_out / P_in);
      const dh_ideal = cp * T_in_K * (1 - Math.pow(pratio, (gamma - 1) / gamma));
      const deltaH = -dh_ideal * efficiency;
      // IAPWS lookup failed, so a property lookup at the outlet would be
      // unreliable. Per CLAUDE.md guardrails: flag the missing diagnostic
      // rather than inventing a quality. exitQuality/wetnessFraction carry the
      // dry sentinel (>1 / 0) and wetnessUnavailable=1 marks them as not solved.
      const metadata = {
        W_turbine: -deltaH,
        fallback: 1,
        exitQuality: 2,
        wetnessFraction: 0,
        wetnessExceedsLimit: 0,
        wetnessUnavailable: 1,
      };
      this.params._lastMetadata = metadata;

      return {
        deltaP: P_out - P_in,
        deltaH,
        momentumSource: 0,
        energySource: 0,
        metadata,
      };
    }
  }
}
